
import { AppSettings } from './types';

// Options for date and time formatting
export interface FormatDateOptions {
  dateStyle?: 'full' | 'long' | 'medium' | 'short';
  timeStyle?: 'full' | 'long' | 'medium' | 'short';
  weekday?: 'long' | 'short' | 'narrow';
  month?: 'numeric' | '2-digit' | 'long' | 'short' | 'narrow';
  day?: 'numeric' | '2-digit';
  year?: 'numeric' | '2-digit';
  hour?: 'numeric' | '2-digit';
  minute?: 'numeric' | '2-digit';
  hour12?: boolean;
}

const getLocale = (settings: AppSettings): string => {
    return settings?.language === 'bg' ? 'bg-BG' : 'en-US';
};

/**
 * Formats a date or date string using the app's current language settings.
 * @param date - The date to format (Date object, ISO string, timestamp number, or Firestore Timestamp).
 * @param settings - The current app settings containing language.
 * @param options - Intl.DateTimeFormat options for customization.
 * @returns A formatted date string.
 */
export const formatDate = (
  date: Date | string | number | { toDate: () => Date } | any,
  settings: AppSettings,
  options: FormatDateOptions = { dateStyle: 'medium', timeStyle: 'short' }
): string => {
  if (!date) return 'N/A';
  
  let dateObj: Date;

  try {
      if (date instanceof Date) {
          dateObj = date;
      } else if (typeof date === 'string' || typeof date === 'number') {
          dateObj = new Date(date);
      } else if (typeof date === 'object' && typeof date.toDate === 'function') {
          // Handle Firestore Timestamp
          dateObj = date.toDate();
      } else {
          // Fallback for unknown objects, attempt to convert or fail gracefully
          dateObj = new Date(date);
      }

      // Check if the resulting date object is valid and has getTime method
      if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
          return 'N/A';
      }
  } catch (e) {
      console.warn("Date parsing error in formatDate:", e);
      return 'N/A';
  }

  const locale = getLocale(settings);
  
  try {
    const formatOptions = { ...options };
    if (settings?.timezone) {
      // @ts-ignore
      formatOptions.timeZone = settings.timezone;
    }
    // @ts-ignore
    return new Intl.DateTimeFormat(locale, formatOptions).format(dateObj);
  } catch (error) {
    console.error("Date formatting error:", error);
    return 'N/A';
  }
};

/**
 * Formats a time string (HH:mm) into a localized 12-hour or 24-hour format.
 * @param time24h - The time string in "HH:mm" format.
 * @param settings - The current app settings.
 * @returns A formatted time string.
 */
export const formatTime = (
    time24h: string,
    settings: AppSettings,
): string => {
    if (!time24h) return '';
    const locale = getLocale(settings);
    const [hour, minute] = time24h.split(':').map(Number);
    const date = new Date();
    date.setHours(hour, minute, 0, 0);
    
    if (isNaN(date.getTime())) return time24h;

    const formatOptions: any = {
        hour: 'numeric',
        minute: '2-digit',
    };
    if (settings?.timezone) {
        formatOptions.timeZone = settings.timezone;
    }

    return new Intl.DateTimeFormat(locale, formatOptions).format(date);
};

/**
 * Extracts a time string (HH:mm) from a date in a specific timezone.
 */
export const getTimeInTimeZone = (date: Date | string | any, timezone: string): string => {
    try {
        let d: Date;
        if (date?.toDate) d = date.toDate();
        else d = typeof date === 'string' ? new Date(date) : date;

        if (!(d instanceof Date) || isNaN(d.getTime())) return '00:00';

        const options: any = {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
            timeZone: timezone
        };
        const parts = new Intl.DateTimeFormat('en-GB', options).formatToParts(d);
        const hour = parts.find(p => p.type === 'hour')?.value;
        const minute = parts.find(p => p.type === 'minute')?.value;
        return `${hour}:${minute}`;
    } catch (e) {
        console.error("getTimeInTimeZone error:", e);
        return '00:00';
    }
};

/**
 * Extracts a date string (YYYY-MM-DD) from a date in a specific timezone.
 */
export const getDateInTimeZone = (date: Date | string | any, timezone: string): string => {
    try {
        let d: Date;
        if (date?.toDate) d = date.toDate();
        else d = typeof date === 'string' ? new Date(date) : date;

        if (!(d instanceof Date) || isNaN(d.getTime())) return '1970-01-01';

        const options: any = {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            timeZone: timezone
        };
        const parts = new Intl.DateTimeFormat('en-GB', options).formatToParts(d);
        const year = parts.find(p => p.type === 'year')?.value;
        const month = parts.find(p => p.type === 'month')?.value;
        const day = parts.find(p => p.type === 'day')?.value;
        return `${year}-${month}-${day}`;
    } catch (e) {
        console.error("getDateInTimeZone error:", e);
        return '1970-01-01';
    }
};

/**
 * Formats a duration in milliseconds into a human-readable string (e.g., "8h 30m").
 * @param milliseconds - The duration in milliseconds.
 * @returns A formatted duration string.
 */
export const formatDuration = (milliseconds: number): string => {
  if (milliseconds < 0 || isNaN(milliseconds)) return '0m';
  
  const totalSeconds = Math.floor(milliseconds / 1000);
  if (totalSeconds < 60) {
    return totalSeconds === 0 ? '0m' : '< 1m';
  }

  const totalMinutes = Math.floor(totalSeconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  
  const parts: string[] = [];
  if (hours > 0) {
    parts.push(`${hours}h`);
  }
  if (minutes > 0) {
    parts.push(`${minutes}m`);
  }
  
  return parts.length > 0 ? parts.join(' ') : '0m';
};

/**
 * Returns a human-readable "time ago" string (e.g., "5m ago", "2h ago").
 */
export const timeAgo = (date: Date | string | number): string => {
  const dateObj = date instanceof Date ? date : new Date(date);
  if (isNaN(dateObj.getTime())) return '--';
  
  const diffMs = Date.now() - dateObj.getTime();
  const mins = Math.floor(diffMs / 60000);
  
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ${mins % 60}m ago`;
  
  const days = Math.floor(hrs / 24);
  return days === 1 ? 'yesterday' : `${days}d ago`;
};
